// Section preview registry (React side). Mirrors the C# SectionManifest for
// the layouts that declare sections, but carries the React-specific knowledge
// of HOW to assemble each section's slots into a live preview. FocusHost reads
// this when the embed URL is focus=section:<id>.
//
// A section is one of two shapes:
//   - "cart": composed through composeCartModal (the real assembled modal, so
//     the preview IS what ships).
//   - generic "stacked": the member slots rendered top-to-bottom inside the
//     section's frame (modal / screen / sheet chrome). Good enough for the
//     series picker + mobile menu where there's no bespoke composition path.
//
// Keep the slot ids + order in sync with SceneDesigner/Models/LayoutManifest.cs
// (SingleMenu.Sections). Drift just means the preview assembles a slightly
// different set than the editor lists — the slots still render individually.

export type SectionFrame = "modal" | "screen" | "sheet";

export interface PreviewSection {
  id: string;
  label: string;
  frame: SectionFrame;
  /** Member slot ids in assembled (top → bottom) order. */
  slotIds: string[];
  /** When set, a bespoke composer builds the preview (e.g. the cart / email
   *  modal). Otherwise the member slots are stacked in the frame. */
  compose?: "cart" | "email";
}

/** Sections previewable in focus mode, keyed by id. Single-menu for v1. */
export const PREVIEW_SECTIONS: Record<string, PreviewSection> = {
  cart: {
    id: "cart",
    label: "Cart",
    frame: "modal",
    slotIds: ["modal:cart", "modal:cart-mobile", "modal:cart-totals", "modal:cart-footer"],
    compose: "cart",
  },
  "series-picker": {
    id: "series-picker",
    label: "Series picker",
    frame: "screen",
    slotIds: ["series-picker-topbar", "series-picker-intro", "series-picker-tiles"],
  },
  "mobile-menu": {
    id: "mobile-menu",
    label: "Mobile menu",
    frame: "sheet",
    slotIds: ["mobile:menu-tabs", "mobile:materials", "mobile:products"],
  },
  email: {
    id: "email",
    label: "Email modal",
    frame: "modal",
    slotIds: ["modal:pdf-email-header", "modal:pdf-email"],
    compose: "email",
  },
};
